import { useState } from 'react';
import { PATHS } from '../../routes/routes';
import { handleShareLink } from '../functions/HandleShareLink';
import { useNavigate } from 'react-router-dom';
import { HOST_API } from '../../api/config';
import { FaEllipsisVertical } from 'react-icons/fa6';
import CopyLinkModal from '../modals/CopyLinkModal';

type MenuCol = {
    drawId: string;
    isPromo?: boolean;
};

const MenuCol = ({ drawId, isPromo = false }: MenuCol) => {
    const navigate = useNavigate();
    const [isCopied, setIsCopied] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [checkingLinks, setCheckingLinks] = useState(false);

    const handleCopyLinkClick = async () => {
        if (checkingLinks) return;
        
        setCheckingLinks(true);
        try {
            // Check if draw has multiple tracking links
            const response = await fetch(`${HOST_API}/public/club-owner/draw/${drawId}`);
            if (!response.ok) {
                throw new Error('Failed to fetch draw data');
            }
            const drawData = await response.json();
            const activeLinks = (drawData.links || []).filter((link: any) => link.isActive);
            
            // If no active tracking links (only default), copy directly
            if (activeLinks.length === 0) {
                const defaultUrl = isPromo 
                    ? PATHS.PROMO_DRAW_SIGNUP.replace(':drawId', drawId)
                    : PATHS.DRAW_INFO.replace(':drawId', drawId);
                
                const fullUrl = window.location.origin + defaultUrl;
                
                // Copy directly to clipboard
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(fullUrl);
                } else {
                    // Fallback for older browsers
                    const textArea = document.createElement('textarea');
                    textArea.value = fullUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                }
                
                // Show copied feedback
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 1500);
            } else {
                // Multiple links available, show modal
                setShowCopyModal(true);
            }
        } catch (error) {
            console.error('Error checking draw links:', error);
            // Fallback to direct copy if API fails
            const defaultUrl = isPromo 
                ? PATHS.PROMO_DRAW_SIGNUP.replace(':drawId', drawId)
                : PATHS.DRAW_INFO.replace(':drawId', drawId);
            
            const fullUrl = window.location.origin + defaultUrl;
            
            try {
                if (navigator.clipboard && window.isSecureContext) {
                    await navigator.clipboard.writeText(fullUrl);
                } else {
                    const textArea = document.createElement('textarea');
                    textArea.value = fullUrl;
                    document.body.appendChild(textArea);
                    textArea.select();
                    document.execCommand('copy');
                    document.body.removeChild(textArea);
                }
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 1500);
            } catch (copyError) {
                console.error('Failed to copy link:', copyError);
            }
        } finally {
            setCheckingLinks(false);
        }
    };

    return (
        <td className="table-cell text-center">
            <div className="dropdown dropdown-top dropdown-end relative">
                <button tabIndex={0} className="menu-button">
                    <FaEllipsisVertical className="text-lg text-gray-400" />
                </button>
                <ul
                    tabIndex={0}
                    className="menu menu-xs dropdown-content bg-base-100 rounded-box z-[100] w-40 px-3 py-2 shadow-lg border-border">
                    <li>
                        <a
                            className="text-sm"
                            onClick={() =>
                                handleShareLink(
                                    'lottery Draw',
                                    'Share this Draw with others',
                                    PATHS.DRAW_INFO.replace(':drawId', drawId)
                                )
                            }>
                            Share
                        </a>
                    </li>
                    <li>
                        <a
                            className="text-sm"
                            onClick={handleCopyLinkClick}>
                            {checkingLinks ? (
                                <span className="text-sm">
                                    <div className="loading loading-spinner loading-xs mr-1 inline-block"></div>
                                    Checking...
                                </span>
                            ) : isCopied ? (
                                <span className="text-success text-sm text-nowrap overflow-hidden text-ellipsis max-w-ful">
                                    Copied to clipboard!
                                </span>
                            ) : (
                                ' Copy Link'
                            )}
                        </a>
                    </li>
                    {isPromo && (
                        <li>
                            <a
                                className="text-sm"
                                onClick={() => window.open(`${HOST_API}/public/qr/draw/${drawId}`, '_blank')}
                            >
                                QR Code
                            </a>
                        </li>
                    )}
                    <li>
                        <a
                            className="text-sm"
                            onClick={() => navigate(PATHS.DRAW_DETAILS.replace(':drawId', drawId))}>
                            Draw Details
                        </a>
                    </li>

                    <li>
                        <a
                            className="text-sm"
                            onClick={() => navigate(PATHS.DRAW_USERS.replace(':drawId', drawId))}>
                            Memberships
                        </a>
                    </li>
                    <li>
                        <a
                            className="text-sm"
                            onClick={() => navigate(
                                isPromo 
                                    ? PATHS.EDIT_PROMO_DRAW.replace(':drawId', drawId)
                                    : PATHS.EDIT_DRAW.replace(':drawId', drawId)
                            )}>
                            Edit Draw
                        </a>
                    </li>
                </ul>
            </div>
            
            <CopyLinkModal
                isOpen={showCopyModal}
                onClose={() => {
                    setShowCopyModal(false);
                    setIsCopied(true);
                    setTimeout(() => setIsCopied(false), 1500);
                }}
                drawId={drawId}
                isPromo={isPromo}
            />
        </td>
    );
};

export default MenuCol;
